import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { MessageCircle, Menu, X, Instagram } from "lucide-react";
import { LOGO_ICON, NAV, SMS_URL, INSTAGRAM_URL } from "@/lib/site";

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
      className={`fixed top-0 inset-x-0 z-50 border-b transition-[background-color,box-shadow,border-color] duration-300 ${
        scrolled ? "bg-offwhite/85 backdrop-blur-xl border-ink/10 shadow-[0_4px_30px_rgba(22,52,69,0.06)]" : "bg-offwhite/60 backdrop-blur-md border-transparent"
      }`}
      data-testid="site-header"
    >
      <div className="max-w-7xl mx-auto px-5 lg:px-12 h-[72px] flex items-center justify-between">
        <a href="#top" className="flex items-center gap-3 group" data-testid="header-logo">
          <img src={LOGO_ICON} alt="C Duarte Home Pro" className="h-12 md:h-[52px] w-auto object-contain transition-transform duration-300 group-hover:scale-105" />
          <span className="font-display font-extrabold tracking-tight leading-none flex flex-col">
            <span className="text-petrol text-base md:text-xl">C DUARTE</span>
            <span className="text-[10px] md:text-[11px] tracking-[0.3em] text-mustard font-bold mt-1">HOME PRO</span>
          </span>
        </a>

        <nav className="hidden md:flex items-center gap-9">
          {NAV.map((n) => (
            <a
              key={n.href}
              href={n.href}
              data-testid={`nav-${n.label.toLowerCase().replace(/\s/g, "-")}`}
              className="text-[13px] font-semibold uppercase tracking-wider text-petrol/70 hover:text-petrol relative after:absolute after:left-0 after:-bottom-1.5 after:h-[2.5px] after:w-0 after:rounded-full after:bg-mustard hover:after:w-full after:transition-[width] after:duration-300"
            >
              {n.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <a
            href={INSTAGRAM_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="header-instagram"
            aria-label="Follow C Duarte Home Pro on Instagram"
            className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-ink/15 text-petrol hover:bg-mustard hover:text-ink hover:border-mustard transition-colors duration-300"
          >
            <Instagram className="h-5 w-5" strokeWidth={2} />
          </a>
          <a
            href={SMS_URL}
            data-testid="header-quote-btn"
            className="hidden sm:inline-flex items-center gap-2 rounded-full bg-mustard px-5 py-2.5 text-sm font-semibold text-ink hover:scale-[1.04] hover:shadow-[0_8px_24px_rgba(232,163,23,0.4)] transition-transform duration-300"
          >
            <MessageCircle className="h-4 w-4" strokeWidth={2.4} />
            Get a Free Quote
          </a>
          <button
            className="md:hidden p-2 text-petrol"
            onClick={() => setOpen((o) => !o)}
            data-testid="mobile-menu-toggle"
            aria-label="Menu"
          >
            {open ? <X /> : <Menu />}
          </button>
        </div>
      </div>

      {open && (
        <motion.div
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: "auto", opacity: 1 }}
          className="md:hidden overflow-hidden border-t border-ink/10 bg-offwhite/95 backdrop-blur-xl"
        >
          <div className="px-5 py-4 flex flex-col gap-1">
            {NAV.map((n) => (
              <a
                key={n.href}
                href={n.href}
                onClick={() => setOpen(false)}
                className="py-3 text-base font-medium text-ink/80 border-b border-ink/5"
                data-testid={`mobile-nav-${n.label.toLowerCase().replace(/\s/g, "-")}`}
              >
                {n.label}
              </a>
            ))}
            <a
              href={SMS_URL}
              className="mt-3 inline-flex items-center justify-center gap-2 rounded-full bg-mustard px-5 py-3 font-semibold text-ink"
            >
              <MessageCircle className="h-4 w-4" /> Get a Free Quote
            </a>
          </div>
        </motion.div>
      )}
    </motion.header>
  );
}
